import { useEffect } from 'react';

interface ServiceDetail {
  icon: string;
  title: string;
  description: string;
  features: string[];
  gradient: string;
  longDescription: string;
  highlights: { icon: string; label: string; value: string }[];
}

interface ServiceModalProps {
  service: ServiceDetail | null;
  onClose: () => void;
}

export default function ServiceModal({ service, onClose }: ServiceModalProps) {
  useEffect(() => {
    if (service) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [service]);

  if (!service) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      <div
        className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${service.gradient} p-8 rounded-t-2xl relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-4">
            <i className={`${service.icon} text-3xl text-white`}></i>
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">{service.title}</h2>
          <p className="text-white/80 text-sm">{service.description}</p>
        </div>

        {/* Body */}
        <div className="p-8">
          <p className="text-gray-600 leading-relaxed mb-8">{service.longDescription}</p>

          {/* Highlights grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {service.highlights.map((h, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <i className={`${h.icon} text-[#DC2626] text-lg`}></i>
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">{h.label}</span>
                </div>
                <p className="text-[#1A365D] font-semibold text-sm">{h.value}</p>
              </div>
            ))}
          </div>

          {/* Features */}
          <h3 className="text-[#1A365D] font-bold text-lg mb-4">What\'s Included</h3>
          <ul className="space-y-3 mb-8">
            {service.features.map((feature, idx) => (
              <li key={idx} className="flex items-start space-x-3 text-sm text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#DC2626] text-lg flex-shrink-0 mt-0.5"></i>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <a
            href="mailto:info@mcallisterdelight.com"
            className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-colors cursor-pointer"
          >
            <i className="ri-mail-line text-lg"></i>
            <span>Request This Service</span>
          </a>
        </div>
      </div>
    </div>
  );
}
